import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/misc';
import { AuthLayout } from '@/components/AuthLayout';
import { PasswordInput } from '@/components/PasswordInput';
import { makeClient } from '@/lib/api';
import { useT } from '@/lib/i18n';

// Página de definição de senha por token (?token=). Serve tanto para "esqueci minha
// senha" quanto para o CONVITE de novo usuário (mesmo link /reset?token=).
export function ResetPassword() {
  const t = useT();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  if (!token) {
    return (
      <AuthLayout subtitle={t('resetPwd.subtitle')}>
        <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-destructive/12">
            <XCircle className="size-7 text-destructive" />
          </div>
          <h2 className="text-xl font-bold">{t('resetPwd.invalidTitle')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t('resetPwd.invalidBody')}</p>
          <Button asChild variant="outline" className="mt-6 w-full"><Link to="/forgot">{t('resetPwd.requestNew')}</Link></Button>
        </div>
      </AuthLayout>
    );
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { toast.error(t('auth.passwordTooShort')); return; }
    if (password !== confirm) { toast.error(t('auth.passwordMismatch')); return; }
    setBusy(true);
    try {
      const { error } = await makeClient().POST('/auth/reset', { body: { token, password } });
      if (error) { toast.error(t('resetPwd.invalidBody')); return; }
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthLayout subtitle={t('resetPwd.subtitle')}>
        <div className="rounded-xl border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-[#3c9a18]/12">
            <CheckCircle2 className="size-7 text-[#3c9a18]" />
          </div>
          <h2 className="text-xl font-bold">{t('resetPwd.successTitle')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t('resetPwd.successBody')}</p>
          <Button asChild className="mt-6 w-full"><Link to="/login">{t('auth.enter')}</Link></Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout subtitle={t('resetPwd.subtitle')}>
      <div className="rounded-xl border bg-card p-6 shadow-sm">
        <form onSubmit={submit} className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">{t('resetPwd.intro')}</p>
          <div className="flex flex-col gap-2">
            <Label htmlFor="password">{t('resetPwd.newPassword')}</Label>
            <PasswordInput id="password" autoComplete="new-password" required minLength={8} autoFocus value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t('auth.minChars')} />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="confirm">{t('auth.confirmPassword')}</Label>
            <PasswordInput id="confirm" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={t('auth.confirmPlaceholder')} />
          </div>
          <Button type="submit" disabled={busy} className="w-full">
            {busy && <Spinner className="border-primary-foreground/40 border-t-primary-foreground" />}
            {t('resetPwd.submit')}
          </Button>
        </form>
      </div>
      <Link
        to="/login"
        className="mt-4 block w-full text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        {t('forgot.backToLogin')}
      </Link>
    </AuthLayout>
  );
}
